import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  Cpu,
  Download,
  FileSpreadsheet,
  FileText,
  Link2,
  Loader2,
  MapPin,
  RefreshCw,
  RotateCcw,
  Settings,
  Shield,
  Upload,
  X,
} from "lucide-react";
import GlowCard from "../components/GlowCard";
import StatusBadge from "../components/StatusBadge";
import { decideFinding } from "./api";
import {
  ComplianceFinding,
  ComplianceRun,
  ENGINE_LABELS,
  FINDING_STATUS_LABELS,
  FindingSeverity,
  FindingStatus,
  ReviewCycleCounts,
  SEVERITY_LABELS,
  VerityProject,
  reviewersOf,
} from "./types";

interface FindingsProps {
  project: VerityProject;
  /** The run to review. Absent until the Compliance Engine has been run. */
  run: ComplianceRun | null;
  onRun?: (run: ComplianceRun) => void;
  /** Send them to the stage that produces findings — also the Re-check door. */
  onRunCompliance?: () => void;
  /** Send them to Deliverables to submit a corrected pack. */
  onUploadRevision?: () => void;
  /**
   * A run has finished but its result has not been read yet.
   *
   * Held shut on purpose. The Compliance Engine's result card is what says how much of the standard
   * the run could assess and how much it could not — ninety findings out of 743 checked with 543
   * unassessable is a different thing from ninety out of ninety, and arriving straight at the list
   * loses that. One press on "Review findings" is the whole gate.
   */
  gated?: boolean;
}

const SEVERITY_TONE: Record<FindingSeverity, "fail" | "warning" | "info"> = {
  critical: "fail",
  major: "warning",
  minor: "info",
};

/** Ranked, so the list opens on what matters rather than on what happened to be raised first. */
const SEVERITY_RANK: Record<FindingSeverity, number> = { critical: 0, major: 1, minor: 2 };

type Filter = "all" | "breach" | "unresolved" | FindingSeverity | FindingStatus;

/**
 * Breaches first and by default.
 *
 * "All" is deliberately NOT the landing filter. A run over a whole standard raises findings at
 * roughly four in five rules, and almost all of them are unresolved rather than wrong — so a list
 * that opens on everything buries the twenty that matter under six hundred that only ask to be
 * looked at. Unresolved has its own chip and its own count.
 */
const FILTERS: { key: Filter; label: string }[] = [
  // "All" reads first because it is the widest — a row of chips that starts partway down its own
  // range makes a reader hunt for the one that shows everything. Where it SITS and what it OPENS on
  // are separate questions: the landing filter is still Breaches, set in useState below.
  { key: "all", label: "All" },
  { key: "breach", label: "Breaches" },
  { key: "unresolved", label: "Unresolved" },
  { key: "open", label: "Open" },
  { key: "critical", label: "Critical" },
  { key: "major", label: "Major" },
  { key: "minor", label: "Minor" },
  { key: "accepted", label: "Accepted" },
  { key: "dismissed", label: "Dismissed" },
];

/** What part of a sheet the quote came from, in a reviewer's words rather than the engine's. */
const REGION_LABELS: Record<string, string> = {
  notes: "notes",
  "title-block": "title block",
  labels: "labels and annotations",
  sweep: "per-sheet search",
};

function statusOf(finding: ComplianceFinding): FindingStatus {
  return finding.status ?? "open";
}

/** 1st, 2nd, 3rd — the review cycle as a person would say it. */
function ordinal(n: number): string {
  const teens = n % 100;
  if (teens >= 11 && teens <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}

/**
 * One cycle as a line: what it reached, and the four numbers it reached it with.
 *
 * The same renderer for a frozen cycle and the live one, so a history line and the tiles beneath it
 * can never disagree about what "reviewed" counts.
 */
const CycleLine = ({
  cycle,
  counts,
  superseded,
}: {
  cycle: number;
  counts: ReviewCycleCounts;
  /** A cycle a revision has already replaced. Dimmed: it is the record, not the work. */
  superseded?: boolean;
}) => (
  <div className={`vr-gate-chain${superseded ? " vr-gate-chain-past" : ""}`}>
    <StatusBadge
      status={counts.open === 0 ? "pass" : "warning"}
      label={`Reviewed (${ordinal(cycle)})`}
    />
    <span className="vr-gate-bit">
      Breaches <strong className="vr-crit">{counts.breach}</strong>
    </span>
    <span className="vr-gate-bit">
      Unresolved <strong className="vr-major">{counts.unresolved}</strong>
    </span>
    <span className="vr-gate-bit">
      Critical <strong className="vr-crit">{counts.critical}</strong>
    </span>
    <span className="vr-gate-bit">
      Reviewed <strong className="vr-done-num">{counts.reviewed}</strong>
    </span>
  </div>
);

/**
 * Findings Review — where a claim becomes a decision.
 *
 * Everything the engine raised, ranked by severity, each carrying the clause it breaches, the
 * document it was read from and how sure the engine was. A reviewer accepts it, which means it
 * stands and goes back to the supplier, or dismisses it with a reason.
 *
 * The reason is required on a dismissal and never on an acceptance. Dismissals are the ones that get
 * read back and questioned — a finding waved away with no note cannot be told apart from one nobody
 * looked at properly.
 */
const Findings = ({
  project,
  run,
  gated = false,
  onRun,
  onRunCompliance,
  onUploadRevision,
}: FindingsProps) => {
  const [filter, setFilter] = useState<Filter>("breach");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The finding being dismissed, and the reason being typed for it. */
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [note, setNote] = useState("");

  /** The cycles this run supersedes, oldest first. Empty until a revision has been re-checked. */
  const priorCycles = run?.priorCycles ?? [];

  /** One line, dot-separated, skipping whatever the project does not carry. */
  const identity = useMemo(() => {
    const reviewers = reviewersOf(project);
    return [
      project.projectRef,
      project.supplier,
      reviewers.length > 0
        ? `${reviewers.length === 1 ? "Reviewer" : "Reviewers"}: ${reviewers
            .map((r) => r.name)
            .join(", ")}`
        : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }, [project]);

  const findings = useMemo(
    () =>
      [...(run?.findings ?? [])].sort(
        (a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity],
      ),
    [run],
  );

  const counts = useMemo(
    () => ({
      total: findings.length,
      open: findings.filter((f) => statusOf(f) === "open").length,
      accepted: findings.filter((f) => statusOf(f) === "accepted").length,
      dismissed: findings.filter((f) => statusOf(f) === "dismissed").length,
      critical: findings.filter((f) => f.severity === "critical").length,
      breach: findings.filter((f) => (f.kind ?? "breach") === "breach").length,
      unresolved: findings.filter((f) => f.kind === "unresolved").length,
    }),
    [findings],
  );

  const visible = useMemo(
    () =>
      findings.filter((f) => {
        if (filter === "all") return true;
        // A finding from before the distinction existed carries no kind, and reads as a breach —
        // which is what it was taken to be when it was raised.
        if (filter === "breach") return (f.kind ?? "breach") === "breach";
        if (filter === "unresolved") return f.kind === "unresolved";
        if (filter === "open" || filter === "accepted" || filter === "dismissed") {
          return statusOf(f) === filter;
        }
        return f.severity === filter;
      }),
    [findings, filter],
  );

  const decide = async (finding: ComplianceFinding, status: FindingStatus, reason?: string) => {
    setBusyId(finding.id);
    setError(null);
    try {
      const updated = await decideFinding(project.id, finding.id, status, reason);
      onRun?.(updated);
      setDismissing(null);
      setNote("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not record the decision.");
    } finally {
      setBusyId(null);
    }
  };

  // A finished run whose result has not been opened yet. The findings exist and are perfectly
  // safe — this is a deliberate stop, not a loading state, so it says what to press and why.
  if (gated && run) {
    return (
      <GlowCard hover={false}>
        <div className="vr-soon">
          <div className="vr-soon-icon">
            <Shield size={28} />
          </div>
          <h2 className="vr-h-sec">Read the check result first</h2>
          <p className="vr-muted" style={{ maxWidth: 460 }}>
            The compliance check has finished. Its result card says what was checked, how much of it
            found matching text in the drawings and how long it took — which is the context every
            finding here is read in. Go back to the Compliance Engine and press{" "}
            <strong>Review findings</strong>.
          </p>
          {onRunCompliance && (
            <button className="vr-btn vr-btn-glow" onClick={onRunCompliance}>
              <Shield size={14} /> Back to Compliance Engine
            </button>
          )}
        </div>
      </GlowCard>
    );
  }

  if (!run || findings.length === 0) {
    return (
      <GlowCard hover={false}>
        <div className="vr-soon">
          <div className="vr-soon-icon">
            <AlertTriangle size={28} />
          </div>
          <h2 className="vr-h-sec">No findings yet</h2>
          <p className="vr-muted" style={{ maxWidth: 460 }}>
            Findings are raised by the compliance engine. Run a check on the Compliance Engine step
            and everything it raises will land here for review.
          </p>
          {onRunCompliance && (
            <button className="vr-btn vr-btn-glow" onClick={onRunCompliance}>
              <Shield size={14} /> Go to Compliance Engine
            </button>
          )}
        </div>
      </GlowCard>
    );
  }

  return (
    // THE FRAME IS PINNED; ONLY THE LIST MOVES. Heading, counts and filter chips are what you steer
    // by — scrolling them away on a list of six hundred means scrolling back up to change a filter
    // or to see whether the number you are working through has moved. Same pattern the Requirements
    // workbench and the project list use, applied to the one screen that most needed it.
    <div className="vr-findings-fixed">
      {/* WHAT WAS CHECKED, ON ONE LINE, ALWAYS VISIBLE.
          The submission, who is reviewing it, where the review got to and what it found — the chain
          a reader needs to know what the list underneath is about. Not an expander: it is the
          heading of the work rather than a detail about it, and a chain you have to open is a chain
          nobody reads.

          IT DESCRIBES THIS CYCLE. When a corrected pack is uploaded and re-checked, this becomes the
          record of the review that preceded it — see the note in the resubmission work about runs
          needing their own history rather than overwriting each other. */}
      <GlowCard className="vr-gate" hover={false}>
        {/* The name carries the box; the reference, supplier and reviewer list identify it but are
            not what a reviewer reads on the way to the list, so they fold behind the ⓘ. */}
        <div className="vr-gate-top">
          <div className="vr-gate-main">
            <span className="vr-gate-name">{project.name}</span>
            {identity && <div className="vr-gate-detail">{identity}</div>}

            {/* ONE LINE PER CYCLE, OLDEST FIRST.
                The same four numbers as the tiles below, in the same colours, so the pair reads as
                one statement rather than two. Smaller here: this line is the caption, the tiles are
                the headline.

                A superseded cycle's line is frozen — the counts it reached and the decisions the
                reviewer had made on it when the revision arrived. The last line is this run, and it
                moves as findings are decided. Two lines is the whole story of a resubmission:
                "Reviewed (1st): 23 breaches" above "Reviewed (2nd): 4 breaches". */}
            {priorCycles.map((cycle) => (
              <CycleLine key={cycle.cycle} cycle={cycle.cycle} counts={cycle.counts} superseded />
            ))}
            <CycleLine
              cycle={priorCycles.length + 1}
              counts={{
                breach: counts.breach,
                unresolved: counts.unresolved,
                critical: counts.critical,
                reviewed: counts.accepted + counts.dismissed,
                open: counts.open,
              }}
            />
          </div>

          {/* Doors to the stages that own these acts, not a second way of doing them — one place to
              upload a deliverable, one place to run a check. Centred against the whole block rather
              than hung off the name, so they read as the box's actions. */}
          <span className="vr-gate-buttons">
            {onUploadRevision && (
              <button className="vr-btn vr-btn-sm vr-btn-outline" onClick={onUploadRevision}>
                <Upload size={13} /> Upload revision
              </button>
            )}
            {onRunCompliance && (
              <button className="vr-btn vr-btn-sm vr-btn-glow" onClick={onRunCompliance}>
                <RefreshCw size={13} /> Re-check
              </button>
            )}
          </span>
        </div>
      </GlowCard>

      {/* Breaches and unresolved are counted apart, because they are different work: one is a
          defect to act on, the other a question to answer. A single "Findings" total hid twenty
          real ones inside six hundred. */}
      <div className="vr-grid vr-grid-4">
        <GlowCard className="vr-stat" hover={false}>
          <p className="vr-stat-value vr-crit">{counts.breach}</p>
          <p className="vr-stat-label">Breaches</p>
        </GlowCard>
        <GlowCard className="vr-stat" hover={false}>
          <p className="vr-stat-value vr-major">{counts.unresolved}</p>
          <p className="vr-stat-label">Unresolved</p>
        </GlowCard>
        <GlowCard className="vr-stat" hover={false}>
          <p className="vr-stat-value vr-crit">{counts.critical}</p>
          <p className="vr-stat-label">Critical</p>
        </GlowCard>
        <GlowCard className="vr-stat" hover={false}>
          <p className="vr-stat-value vr-done-num">{counts.accepted + counts.dismissed}</p>
          <p className="vr-stat-label">Reviewed</p>
        </GlowCard>
      </div>

      {error && <div className="vr-form-error">{error}</div>}

      <div className="vr-chips">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={`vr-chip-btn ${filter === f.key ? "vr-active" : ""}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="vr-finding-list">
        {visible.map((finding) => {
          const status = statusOf(finding);
          const busy = busyId === finding.id;
          const isDismissing = dismissing === finding.id;

          return (
            <GlowCard
              key={finding.id}
              className={`vr-finding vr-finding-${status}`}
              hover={false}
            >
              <div className="vr-finding-top">
                <StatusBadge
                  status={SEVERITY_TONE[finding.severity]}
                  label={SEVERITY_LABELS[finding.severity]}
                />
                <span className="vr-find-id">{finding.id}</span>
                <span className={`vr-engine-tag vr-engine-${finding.engine}`}>
                  {finding.engine === "deterministic" ? <Settings size={12} /> : <Cpu size={12} />}
                  {ENGINE_LABELS[finding.engine]}
                </span>
                <div className="vr-conf">
                  <span className="vr-conf-track">
                    <span className="vr-conf-fill" style={{ width: `${finding.confidence}%` }} />
                  </span>
                  <span className="vr-conf-num">{finding.confidence}%</span>
                </div>
                {status !== "open" && (
                  <span className={`vr-decision vr-decision-${status}`}>
                    {status === "accepted" ? <Check size={12} /> : <X size={12} />}
                    {FINDING_STATUS_LABELS[status]}
                  </span>
                )}
              </div>

              <h4 className="vr-finding-title">{finding.title}</h4>
              <p className="vr-finding-detail">{finding.detail}</p>

              <div className="vr-finding-meta">
                <span className="vr-find-clause">{finding.clause}</span>
                <span className="vr-find-source" title={finding.sourceFileName}>
                  <Link2 size={12} />
                  {finding.sourceFileName}
                </span>
              </div>

              {/* WHERE IT WAS READ FROM, VERBATIM.
                  A finding is a claim until somebody can check it, and checking one meant opening
                  the pack and hunting for the sentence. The engine knew the sheet, the page and the
                  exact words all along and was throwing them away at the end. The quote is copied
                  character-for-character — never paraphrased — so what is shown here is what is on
                  the drawing. */}
              {finding.evidence && (
                <div className="vr-evidence">
                  <p className="vr-evidence-head">
                    <MapPin size={12} />
                    {finding.evidence.sheet
                      ? `Sheet ${finding.evidence.sheet}`
                      : finding.evidence.where}
                    {finding.evidence.page ? ` · page ${finding.evidence.page}` : ""}
                    {finding.evidence.region && finding.evidence.region !== "sweep"
                      ? ` · ${REGION_LABELS[finding.evidence.region]}`
                      : ""}
                  </p>

                  {finding.evidence.quote && (
                    <p className="vr-evidence-quote">“{finding.evidence.quote}”</p>
                  )}

                  {/* The engine's own sweep across every sheet, not the model's reading of one.
                      "Absent from 4 of 30" is the arithmetic behind a per-sheet finding, and it is
                      what a reviewer would otherwise have to redo by hand. */}
                  {finding.evidence.absentFrom && finding.evidence.absentFrom.length > 0 && (
                    <p className="vr-evidence-sweep">
                      Absent from {finding.evidence.absentFrom.length} of{" "}
                      {finding.evidence.sheetsSearched} sheets searched:{" "}
                      <span className="vr-cell-code">
                        {finding.evidence.absentFrom.slice(0, 12).join(", ")}
                        {finding.evidence.absentFrom.length > 12 ? " …" : ""}
                      </span>
                    </p>
                  )}
                </div>
              )}

              {/* The reason a dismissal was made, kept visible rather than buried in the record. */}
              {status === "dismissed" && finding.reviewNote && (
                <p className="vr-finding-note">
                  <strong>Dismissed:</strong> {finding.reviewNote}
                </p>
              )}

              {isDismissing ? (
                <div className="vr-dismiss-box">
                  <label htmlFor={`note-${finding.id}`}>Why is this not a non-conformance?</label>
                  <input
                    id={`note-${finding.id}`}
                    className="vr-input"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. superseded by Rev C, issued 12 Aug"
                    autoFocus
                  />
                  <div className="vr-finding-actions">
                    <button
                      className="vr-btn vr-btn-sm vr-btn-ghost"
                      onClick={() => {
                        setDismissing(null);
                        setNote("");
                      }}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                    <button
                      className="vr-btn vr-btn-sm vr-btn-outline"
                      onClick={() => void decide(finding, "dismissed", note.trim())}
                      disabled={busy || note.trim().length === 0}
                    >
                      {busy ? <Loader2 size={13} className="vr-spin-inline" /> : <X size={13} />}
                      Confirm dismissal
                    </button>
                  </div>
                </div>
              ) : (
                <div className="vr-finding-actions">
                  {status === "open" ? (
                    <>
                      <button
                        className="vr-btn vr-btn-sm vr-btn-outline"
                        onClick={() => {
                          setDismissing(finding.id);
                          setNote("");
                        }}
                        disabled={busy}
                      >
                        <X size={13} /> Dismiss
                      </button>
                      <button
                        className="vr-btn vr-btn-sm vr-btn-glow"
                        onClick={() => void decide(finding, "accepted")}
                        disabled={busy}
                      >
                        {busy ? <Loader2 size={13} className="vr-spin-inline" /> : <Check size={13} />}
                        Accept finding
                      </button>
                    </>
                  ) : (
                    <button
                      className="vr-btn vr-btn-sm vr-btn-ghost"
                      onClick={() => void decide(finding, "open")}
                      disabled={busy}
                      title="Return this finding to review"
                    >
                      {busy ? (
                        <Loader2 size={13} className="vr-spin-inline" />
                      ) : (
                        <RotateCcw size={13} />
                      )}
                      Reopen
                    </button>
                  )}
                </div>
              )}
            </GlowCard>
          );
        })}

        {visible.length === 0 && (
          <div className="vr-empty">Nothing under that filter.</div>
        )}
      </div>
    </div>
  );
};

export default Findings;
